export const WIZARD_STEP_IDS = [
  'welcome',
  'outcomes',
  'departments',
  'users',
  'resolve',
  'functions',
  'teams',
  'systems',
  'processes',
  'overview'
] as const;

export type WizardStepId = (typeof WIZARD_STEP_IDS)[number];

/**
 * Server-derived wizard progress. Per-step "done" flags mirror the
 * organization's initialization timestamps ("data imported at least once");
 * completedAt is the overall Abschließen stamp. Skip is never stored — a
 * skipped step is simply not done while completedAt is set.
 */
export interface WizardProgress {
  departmentsDone: boolean;
  usersDone: boolean;
  orgFunctionsDone: boolean;
  teamsDone: boolean;
  systemsDone: boolean;
  processesDone: boolean;
  completedAt: number | undefined;
  unresolvedDepartments: number;
  /** Departments with a manager already assigned — drives the Manager-auflösen rail entry staying visible/done instead of vanishing once nothing is left open. */
  resolvedDepartments: number;
}

export interface WizardStepDef {
  id: WizardStepId;
  /** 1-based number on the rail spine; undefined = not its own rail entry */
  railNumber?: number;
  /** Rendered as an indented sub-item under this rail step */
  subStepOf?: WizardStepId;
  /** Rail label; omitted for intro steps that never render on the rail */
  labelKey?: string;
  /** Short rail sub-line under the step title */
  descriptionKey?: string;
  /** Überspringen offered (pure navigation, nothing written) */
  skippable: boolean;
  /** Omitted for intro steps, which have no "done" notion */
  isDone?(progress: WizardProgress): boolean;
  isReachable(progress: WizardProgress): boolean;
  /** Hidden from the rail entirely when false; defaults to visible */
  isVisible?(progress: WizardProgress): boolean;
  /** Footer Weiter-button label override (defaults to initialSetup.footer.next) */
  nextLabelKey?: string;
  /** Weiter-button icon: a leading check for Abschließen, else the trailing arrow */
  nextIcon?: 'arrow' | 'check';
  /** Chrome-less full-width intro screen rendered without the step rail */
  isIntro?: boolean;
  /** Structural anchor (departments/users): an untouched seeded table is pure navigation */
  isAnchor?: boolean;
}

const anchorDone = (progress: WizardProgress) => progress.departmentsDone && progress.usersDone;

const resolveOpen = (progress: WizardProgress) =>
  progress.usersDone && progress.unresolvedDepartments > 0;

// Once a manager has been resolved manually, the sub-step stays on the rail
// (as done) instead of vanishing the instant the last open department clears
// — a user revisiting it afterwards can still see/change the assignment.
const resolveVisible = (progress: WizardProgress) =>
  progress.usersDone && (progress.unresolvedDepartments > 0 || progress.resolvedDepartments > 0);

// Every step past the Manager-auflösen sub-step stays locked while
// resolutions are open: the sub-step is not skippable, and a departments
// edit can stage new unresolved manager emails at ANY time — also long
// after the users step is done. clampToReachable then lands every entry
// path (rail, Überspringen, Weiter, deep links, history) on 'resolve' via
// deriveResumeStep; steps before the sub-step stay freely reachable.
const reachablePastResolve = (progress: WizardProgress) =>
  anchorDone(progress) && !resolveOpen(progress);

export const WIZARD_STEPS: WizardStepDef[] = [
  {
    id: 'welcome',
    skippable: false,
    isReachable: () => true,
    isIntro: true
  },
  {
    id: 'outcomes',
    skippable: false,
    isReachable: () => true,
    isIntro: true,
    nextLabelKey: 'initialSetup.footer.start'
  },
  {
    id: 'departments',
    railNumber: 1,
    labelKey: 'initialSetup.steps.departments',
    descriptionKey: 'initialSetup.stepDescriptions.departments',
    skippable: false,
    isDone: (progress) => progress.departmentsDone,
    isReachable: () => true,
    isAnchor: true
  },
  {
    id: 'users',
    railNumber: 2,
    labelKey: 'initialSetup.steps.employees',
    descriptionKey: 'initialSetup.stepDescriptions.employees',
    skippable: false,
    isDone: (progress) => progress.usersDone,
    isReachable: (progress) => progress.departmentsDone,
    isAnchor: true
  },
  {
    id: 'resolve',
    subStepOf: 'users',
    labelKey: 'initialSetup.steps.resolveManagers',
    descriptionKey: 'initialSetup.stepDescriptions.resolveManagers',
    skippable: false,
    isDone: (progress) => progress.usersDone && progress.unresolvedDepartments === 0,
    // Visible from the first resolution onward: appearing once (an open
    // department, or a manual resolution), it stays on the rail — reachable
    // so a user can click back in even after everything is resolved. Before
    // that it stays hidden and deep links clamp past it.
    isReachable: resolveVisible,
    isVisible: resolveVisible
  },
  {
    id: 'functions',
    railNumber: 3,
    labelKey: 'initialSetup.steps.functions',
    descriptionKey: 'initialSetup.stepDescriptions.functions',
    skippable: true,
    isDone: (progress) => progress.orgFunctionsDone,
    isReachable: reachablePastResolve
  },
  {
    id: 'teams',
    railNumber: 4,
    labelKey: 'initialSetup.steps.teams',
    descriptionKey: 'initialSetup.stepDescriptions.teams',
    skippable: true,
    isDone: (progress) => progress.teamsDone,
    isReachable: reachablePastResolve
  },
  {
    id: 'systems',
    railNumber: 5,
    labelKey: 'initialSetup.steps.systems',
    descriptionKey: 'initialSetup.stepDescriptions.systems',
    skippable: true,
    isDone: (progress) => progress.systemsDone,
    isReachable: reachablePastResolve
  },
  {
    id: 'processes',
    railNumber: 6,
    labelKey: 'initialSetup.steps.processes',
    descriptionKey: 'initialSetup.stepDescriptions.processes',
    skippable: true,
    isDone: (progress) => progress.processesDone,
    isReachable: reachablePastResolve
  },
  {
    id: 'overview',
    labelKey: 'initialSetup.steps.overview',
    descriptionKey: 'initialSetup.stepDescriptions.overview',
    skippable: false,
    isDone: (progress) => !!progress.completedAt,
    isReachable: reachablePastResolve,
    nextLabelKey: 'initialSetup.footer.finish',
    nextIcon: 'check'
  }
];

export const wizardStepDef = (id: WizardStepId): WizardStepDef =>
  WIZARD_STEPS.find((step) => step.id === id)!;

export const nextStepId = (id: WizardStepId): WizardStepId | undefined =>
  WIZARD_STEP_IDS[WIZARD_STEP_IDS.indexOf(id) + 1];

/**
 * Nearest reachable predecessor — skips steps that are currently
 * unreachable (e.g. a hidden Manager-auflösen sub-step), so Zurück never
 * dead-ends on a clamp back to the current step.
 */
export const previousStepId = (
  id: WizardStepId,
  progress?: WizardProgress
): WizardStepId | undefined => {
  for (let i = WIZARD_STEP_IDS.indexOf(id) - 1; i >= 0; i--) {
    const candidate = WIZARD_STEP_IDS[i];
    if (!progress || wizardStepDef(candidate).isReachable(progress)) return candidate;
  }
  return undefined;
};

/**
 * Where a returning visit lands: the first step that still needs attention.
 * The structural anchor (departments -> users -> open manager resolutions)
 * comes first; afterwards the first not-done optional step, else the
 * overview.
 */
export function deriveResumeStep(progress: WizardProgress): WizardStepId {
  if (!progress.departmentsDone) return 'welcome';
  if (!progress.usersDone) return 'users';
  if (resolveOpen(progress)) return 'resolve';
  const firstOpenOptional = (['functions', 'teams', 'systems', 'processes'] as const).find(
    (id) => !(wizardStepDef(id).isDone?.(progress) ?? false)
  );
  return firstOpenOptional ?? 'overview';
}

/**
 * Deep links keep working after refresh, but a step whose prerequisites are
 * missing falls back to the resume step instead of rendering broken.
 */
export function clampToReachable(id: WizardStepId, progress: WizardProgress): WizardStepId {
  return wizardStepDef(id).isReachable(progress) ? id : deriveResumeStep(progress);
}
